import type { ReactNode } from 'react';
import { CircleCheck, ClipboardList, Zap, type LucideIcon } from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { cn } from '@/lib/utils';
import type { RegistrationMode, RegistrationSectionId } from './registration-rules';

export type SectionAnchorId = RegistrationSectionId | 'insurance';

export function sectionAnchor(id: SectionAnchorId): string {
  return `registration-section-${id}`;
}

export function RegistrationSection({
  id,
  title,
  description,
  icon: Icon,
  badge,
  done,
  emphasis,
  columns = 2,
  children,
}: {
  id: SectionAnchorId;
  title: string;
  description?: string;
  icon: LucideIcon;
  badge?: ReactNode;
  done?: boolean;
  emphasis?: boolean;
  columns?: 1 | 2 | 3;
  children: ReactNode;
}) {
  return (
    <section
      id={sectionAnchor(id)}
      aria-labelledby={`${sectionAnchor(id)}-title`}
      className={cn(
        'scroll-mt-6 rounded-xl border bg-card shadow-sm transition-[border-color,box-shadow] duration-300',
        'motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 motion-safe:duration-300',
        emphasis && 'border-warning/50 ring-2 ring-warning/20',
      )}
    >
      <header className="flex items-start gap-3 border-b px-4 py-3 sm:px-5">
        <span
          className={cn(
            'grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors',
            emphasis ? 'bg-warning/15 text-warning' : done ? 'bg-success/15 text-success' : 'bg-primary/10 text-primary',
          )}
        >
          <Icon className="h-[1.125rem] w-[1.125rem]" aria-hidden />
        </span>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id={`${sectionAnchor(id)}-title`} className="text-sm font-semibold leading-tight first-letter:uppercase">
              {title}
            </h2>
            {badge}
          </div>
          {description && <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{description}</p>}
        </div>
        {done && <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-success motion-safe:animate-in motion-safe:zoom-in-50" aria-label={tr('opd', 'section_complete')} />}
      </header>
      <div
        className={cn(
          'grid grid-cols-1 gap-x-4 gap-y-4 px-4 py-4 sm:px-5',
          columns === 2 && 'sm:grid-cols-2',
          columns === 3 && 'sm:grid-cols-2 xl:grid-cols-3',
        )}
      >
        {children}
      </div>
    </section>
  );
}

const MODE_OPTIONS: { value: RegistrationMode; label: string; hint: string; icon: LucideIcon }[] = [
  { value: 'quick', label: 'registration_mode_quick', hint: 'registration_mode_quick_hint', icon: Zap },
  { value: 'full', label: 'registration_mode_full', hint: 'registration_mode_full_hint', icon: ClipboardList },
];

export function ModeSwitch({ mode, onChange }: { mode: RegistrationMode; onChange: (mode: RegistrationMode) => void }) {
  return (
    <div role="radiogroup" aria-label={tr('opd', 'registration_mode')} className="grid grid-cols-2 gap-1.5 rounded-2xl border bg-muted/40 p-1.5 dark:bg-muted/20">
      {MODE_OPTIONS.map((option) => {
        const selected = option.value === mode;
        const Icon = option.icon;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => {
              if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
              event.preventDefault();
              const next = option.value === 'quick' ? 'full' : 'quick';
              onChange(next);
              event.currentTarget.parentElement?.querySelector<HTMLButtonElement>(`[data-mode="${next}"]`)?.focus();
            }}
            data-mode={option.value}
            className={cn(
              'group flex min-w-0 items-center gap-3 rounded-xl px-3 py-2.5 text-start transition-[background-color,box-shadow,color] duration-200 focus-ring',
              selected ? 'bg-card shadow-sm ring-1 ring-border' : 'text-muted-foreground hover:bg-card/60 hover:text-foreground',
            )}
          >
            <span
              className={cn(
                'grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors duration-200',
                selected ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/30' : 'bg-muted text-muted-foreground group-hover:text-foreground',
              )}
            >
              <Icon className="h-[1.125rem] w-[1.125rem]" aria-hidden />
            </span>
            <span className="min-w-0">
              <span className={cn('block text-sm font-semibold leading-tight', selected && 'text-foreground')}>{tr('opd', option.label)}</span>
              <span className="mt-0.5 hidden text-xs leading-snug text-muted-foreground sm:block">{tr('opd', option.hint)}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
